import { z } from 'zod';

// The Amazon door-access listing, on `GET /app_service/panel/v1/amazon/door-access/locations`.
// Delivered by CBX-11121 and mirrored from the realized shape.
//
// The envelope is an OBJECT with a single `locations` key — never a bare array, which is the shape
// the endpoint does not send and the one an implementer would otherwise assume.
//
// Rows are `unknown` on purpose: this read discards every one of them to return a length, and a
// wave-sized answer is thousands of objects that would be allocated twice for a single number.
// Per-row validation belongs to the single-location read, which actually renders row fields.
//
// `.strict()` — the ONE strict schema in this package, and the reason is specific enough that it
// should not be copied elsewhere. A count taken from `.length` is only the truth while the endpoint
// returns every matching row, and the delivered contract promises no such thing: no `count`, no cap,
// no cursor. If it ever grows a pager, a tolerant schema keeps parsing and the screen's headline
// silently becomes a PAGE SIZE — a wrong number that still looks right, on the one screen whose
// whole job is that number.
//
// Guarding a `next` key the way the DRF-shaped envelopes here do would not work: this response is
// hand-rolled, so there is no conventional key to guard and a guess would miss the shapes it did not
// guess. Rejecting any unknown key is what avoids guessing.
//
// It is cheap ONLY because this is a one-key wrapper and the rows stay open: backend enriching a
// location still parses. On a rich entity schema the same strictness would break the screen on every
// harmless additive field — do not generalize it. And note the likeliest addition to THIS envelope
// is `count`, which is exactly the thing the panel wants to hear about rather than ignore.
export const AmazonDoorAccessCountSchema = z
  .object({ locations: z.array(z.unknown()) })
  .strict();

export type AmazonDoorAccessCount = z.infer<typeof AmazonDoorAccessCountSchema>;

// The state of ONE location at the Amazon door-access gate, on
// `GET /app_service/panel/v1/amazon/door-access/locations/<location_id>`. Delivered by CBX-11121 and
// mirrored from the realized shape.
//
// **No `.strict()`**, and the difference with the count envelope above is deliberate. There, an
// unknown key means the number might be a page size — a response that looks right and is wrong.
// Here an additive field is inert: this read consumes three fields and paints two. Do not
// generalize the strictness next door.
//
// `updated_at` is a NAIVE ISO-8601 with no offset (`"2026-08-18T12:00:00"`), because app-service
// runs with `USE_TZ = False`. Hence `z.string()` and never `z.coerce.date()` — and hence it is
// transformed AWAY rather than kept: the panel consumes it only as the «never configured» signal,
// and no timezone travels beside it in which to render it.
export const AmazonDoorAccessLocationSchema = z
  .object({
    location_id: z.number().int().positive(),
    enabled: z.boolean(),
    updated_at: z.string().nullable(),
  })
  .transform(({ location_id, enabled, updated_at }) => ({
    locationId: location_id,
    enabled,
    // «Has a row in the table», which is as close to «exists» as this screen can read: the endpoint
    // never 404s, so a location nobody has configured and an id that is no location at all answer
    // exactly the same thing.
    configured: updated_at !== null,
  }));

export type AmazonDoorAccessLocation = z.infer<typeof AmazonDoorAccessLocationSchema>;
